import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { Section, TextoSection } from '../../types/festejo';

interface TextoSectionEditorProps {
  section: Section | null;
  onSave: (section: Section) => void;
  onCancel: () => void;
}

interface FormData {
  title: string;
  content: string;
}

interface FormErrors {
  title?: string;
  content?: string;
}

const TextoSectionEditor: React.FC<TextoSectionEditorProps> = ({
  section,
  onSave,
  onCancel
}) => {
  const [formData, setFormData] = useState<FormData>({
    title: '',
    content: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});

  // Carregar dados da seção se estiver editando
  useEffect(() => {
    if (section && section.type === 'texto') {
      const textoSection = section as TextoSection;
      setFormData({
        title: textoSection.title,
        content: textoSection.content
      });
    }
  }, [section]);

  // Validar formulário
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Título deve ter pelo menos 3 caracteres';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Conteúdo é obrigatório';
    } else if (formData.content.trim().length < 10) {
      newErrors.content = 'Conteúdo deve ter pelo menos 10 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manipular mudanças nos campos
  const handleFieldChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpar erro do campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Salvar seção
  const handleSave = () => {
    if (!validateForm()) return;

    const textoSection: TextoSection = {
      id: section?.id || '',
      type: 'texto',
      title: formData.title.trim(),
      content: formData.content.trim(),
      order: section?.order || 0
    };

    onSave(textoSection);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Use seções de texto para compartilhar a história do padroeiro, tradições do festejo, 
        informações importantes ou qualquer conteúdo descritivo.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Título da Seção"
          value={formData.title}
          onChange={handleFieldChange('title')}
          error={!!errors.title}
          helperText={errors.title || 'Ex: História de São João Batista'}
          required
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          multiline
          rows={12}
          label="Conteúdo"
          value={formData.content}
          onChange={handleFieldChange('content')}
          error={!!errors.content}
          helperText={
            errors.content || 
            'Escreva o conteúdo da seção. Quebras de linha serão preservadas.'
          }
          placeholder="Digite aqui o conteúdo da seção...

Você pode usar quebras de linha para organizar o texto em parágrafos.

Exemplo:
São João Batista foi o precursor de Jesus Cristo, nascido seis meses antes do Salvador. Sua festa é celebrada no dia 24 de junho, marcando o início das tradicionais festas juninas.

As celebrações em honra a São João incluem fogueiras, que simbolizam a luz de Cristo, e diversas tradições populares que unem fé e cultura."
          required
        />
      </Box>

      {/* Preview */}
      <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Preview:
        </Typography>
        <Typography variant="h6" gutterBottom>
          {formData.title || 'Título da seção'}
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            whiteSpace: 'pre-line',
            color: formData.content ? 'text.primary' : 'text.secondary'
          }}
        >
          {formData.content || 'Conteúdo da seção aparecerá aqui...'}
        </Typography>
      </Box>

      {/* Dicas */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>💡 Dicas para uma boa seção de texto:</strong><br/>
          • Use parágrafos para organizar o conteúdo<br/>
          • Inclua informações relevantes e interessantes<br/>
          • Mantenha uma linguagem acessível<br/>
          • Conte histórias que conectem com a comunidade
        </Typography>
      </Alert>

      {/* Botões */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<Cancel />}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={<Save />}
        >
          Salvar Seção
        </Button>
      </Box>
    </Box>
  );
};

export default TextoSectionEditor;

